/**
 * A valve's run time is typed into two boxes, and a half-typed pair is not a
 * run time.
 *
 * The schedule builder fires a PUT on every keystroke. Clearing the minutes
 * box to retype it composes 0 with an empty seconds box, and the API floors
 * `duration_sec` at 1 — so the old code discovered the invalid value by
 * sending it and catching the 400. Three of those landed in production on
 * 2026-09-22, interleaved with the successful keystrokes either side.
 *
 * The 400 was not the damage. The builder writes optimistically, and a
 * failed PUT does not take that write back, so the program on screen sat
 * there showing a run time the server had refused to store. On an irrigation
 * schedule that divergence is the whole problem: the page says four minutes,
 * the controller still holds forty, and nothing on screen distinguishes the
 * two once the error banner is cleared by the next successful edit.
 *
 * So composition is a decision, not an arithmetic expression: a pair that
 * does not make at least one second is `null`, and `null` means send nothing.
 */
export const MIN_RELAY_DURATION_SEC = 1;
export const MAX_RELAY_DURATION_SEC = 86400;

/**
 * Compose the minutes and seconds boxes into a duration to save, or `null`
 * when the pair is not yet a value worth sending.
 *
 * Inputs are the raw strings the boxes hold, because "" and "0" have to be
 * told apart: an emptied box is mid-edit, a typed zero is a choice. Both
 * compose to nothing on their own, but only the second one survives a blur.
 */
export function composeRelayDuration(minutes: string, seconds: string): number | null {
  const min = Number.parseInt(minutes, 10);
  const sec = Number.parseInt(seconds, 10);
  // A box holding "" or "-" parses to NaN. That is mid-edit, not zero.
  const total = (Number.isFinite(min) ? min : 0) * 60 + (Number.isFinite(sec) ? sec : 0);
  if (!Number.isFinite(total)) return null;
  if (total < MIN_RELAY_DURATION_SEC) return null;
  if (total > MAX_RELAY_DURATION_SEC) return null;
  return total;
}

/** Split a stored duration back into the two boxes. */
export function splitRelayDuration(durationSec: number): { min: number; sec: number } {
  const safe = Math.max(0, Math.floor(durationSec) || 0);
  return { min: Math.floor(safe / 60), sec: safe % 60 };
}
